import { z } from "zod";
import { CommentSchema } from "./comments.js";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
/**
 * Each item-split division references a participant by `participantId`.
 * Different split types carry different extra data on the division.
 */
export const DivisionAmountsSchema = z.strictObject({
    participantId: z.string().uuid(),
    amount: z.number().int().nonnegative(),
});
export const DivisionEqualSchema = z.strictObject({
    participantId: z.string().uuid(),
});
export const DivisionPercentageSchema = z.strictObject({
    participantId: z.string().uuid(),
    /** Integer percentage; sum across an item must equal 100. */
    value: z.number().int().min(0).max(100),
});
export const DivisionSharesSchema = z.strictObject({
    participantId: z.string().uuid(),
    /** Non-negative integer; cost is divided proportionally to total shares. */
    value: z.number().int().nonnegative(),
});
export const ItemSplitSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("AMOUNTS"),
        divisions: z.array(DivisionAmountsSchema).min(1),
    }),
    z.strictObject({
        type: z.literal("EQUAL"),
        divisions: z.array(DivisionEqualSchema).min(1),
    }),
    z.strictObject({
        type: z.literal("PERCENTAGES"),
        divisions: z.array(DivisionPercentageSchema).min(1),
    }),
    z.strictObject({
        type: z.literal("SHARES"),
        divisions: z.array(DivisionSharesSchema).min(1),
    }),
]);
export const ItemSchema = z.strictObject({
    id: z.string().uuid(),
    label: z.string().min(1),
    /** Smallest currency unit (cents, pence). */
    cost: z.number().int().nonnegative(),
    split: ItemSplitSchema,
});
/**
 * `amount` is in the expense currency; `charge` is in the group currency.
 * For same-currency expenses, `amount === charge`.
 */
export const PaymentSchema = z.strictObject({
    participantId: z.string().uuid(),
    amount: z.number().int().nonnegative(),
    charge: z.number().int().nonnegative(),
});
export const ExpenseSchema = z.strictObject({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    label: z.string().min(1).max(120),
    currency: SupportedCurrencyCodesSchema,
    comments: z.array(CommentSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    payments: z.array(PaymentSchema).min(1),
    items: z.array(ItemSchema).min(1),
    /**
     * Sum of `payments[].charge` — the expense's true cost in the group
     * currency. Equal to the items-cost total when the expense was recorded
     * in the group currency; otherwise reflects what the payers were
     * actually charged on their cards (FX + fees), which is what owed shares
     * are allocated against. Derived server-side, never stored separately.
     */
    groupCharge: z.number().int().nonnegative(),
    /**
     * Optimistic-concurrency token, incremented on every successful update.
     * Returned on reads so clients can echo it back as the required
     * `version` on `UpdateExpenseRequest` (see #246).
     */
    version: z.number().int().positive(),
    /**
     * ParticipantId of the caller who created the expense — the trusted basis
     * for the edit-own authorization check. Optional: expenses created before
     * this attribute was persisted have no recorded creator (see #252).
     */
    createdBy: z.string().uuid().optional(),
});
const ItemInputSchema = z.strictObject({
    label: z.string().min(1),
    cost: z.number().int().nonnegative(),
    split: ItemSplitSchema,
});
/**
 * Update payload may carry the existing `id` to preserve item identity across
 * edits. Items with no `id` are treated as newly added rows.
 */
const UpdateItemInputSchema = z.strictObject({
    id: z.string().uuid().optional(),
    label: z.string().min(1),
    cost: z.number().int().nonnegative(),
    split: ItemSplitSchema,
});
export const CreateExpenseRequestSchema = z.strictObject({
    label: z.string().min(1).max(120),
    currency: SupportedCurrencyCodesSchema,
    payments: z.array(PaymentSchema).min(1),
    items: z.array(ItemInputSchema).min(1),
});
export const UpdateExpenseRequestSchema = z.strictObject({
    label: z.string().min(1).max(120).optional(),
    payments: z.array(PaymentSchema).min(1).optional(),
    items: z.array(UpdateItemInputSchema).min(1).optional(),
    /**
     * Last-known version, for optimistic concurrency. Required: every update is
     * conditioned on it so a client cannot omit it for a last-writer-wins
     * overwrite of `Payments`/`Items` (see #246). A stale value yields 409.
     */
    version: z.number().int().positive(),
});
